import { Agent, AgentListResponse, PromptWithHistory, Prompt, PromptTestResponse, AgentSettings, WhitelistUser } from '@/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// Global 401 handler
let on401Callback: (() => void) | null = null;

export function set401Handler(callback: () => void) {
  on401Callback = callback;
}

async function handleResponse(response: Response): Promise<Response> {
  if (response.status === 401 && on401Callback) {
    on401Callback();
  }
  return response;
}

// Simple error handling wrapper for network requests
export async function fetchWithErrorHandling(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    const response = await fetch(input, init);
    return response;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to the server. Please check your connection.');
    }
    throw error;
  }
}

// Get auth token from localStorage
function getAuthHeader(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
}

// ===== Auth API =====

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  tenantId: string;
  owner: {
    id: string;
    name: string;
    email: string;
  };
}

export async function login(data: LoginRequest): Promise<AuthResponse> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Login failed' }));
    throw new Error(error.message || 'Invalid credentials');
  }

  const apiResponse = await response.json();
  // API returns { token, tenantId }, add owner info for frontend
  return {
    ...apiResponse,
    owner: {
      id: apiResponse.tenantId, // Use tenantId as user ID
      name: '', // Name not available from login response
      email: data.email,
    },
  };
}

export async function register(data: RegisterRequest): Promise<AuthResponse> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Registration failed' }));
    throw new Error(error.message || 'Registration failed');
  }

  const apiResponse = await response.json();
  // API returns { token, tenantId }, add owner info from request for frontend
  return {
    ...apiResponse,
    owner: {
      id: apiResponse.tenantId, // Use tenantId as user ID
      name: data.name,
      email: data.email,
    },
  };
}

export async function refreshToken(): Promise<{ token: string }> {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('No token to refresh');
  }

  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
  );

  if (!response.ok) {
    localStorage.removeItem('auth_token');
    throw new Error('Token refresh failed');
  }

  return response.json();
}

export function logout(): void {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
}

// Agent API
export async function getAgents(params: {
  page?: number;
  pageSize?: number;
  status?: string;
  sortBy?: string;
  sortOrder?: string;
  search?: string;
} = {}): Promise<AgentListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.append('page', params.page.toString());
  if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params.status && params.status !== 'all') searchParams.append('status', params.status);
  if (params.sortBy) searchParams.append('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
  if (params.search) searchParams.append('search', params.search);

  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents?${searchParams}`, {
      headers: getAuthHeader(),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch agents: ${response.statusText}`);
  }

  // API returns array directly, wrap in AgentListResponse format
  const backendAgents = await response.json();
  
  // Convert backend format (snake_case) to frontend format (camelCase)
  const agents = Array.isArray(backendAgents) ? backendAgents.map((agent: any) => ({
    agentId: agent.agent_id,
    ownerId: agent.owner_id,
    name: agent.name,
    displayName: agent.display_name || agent.name,
    description: agent.description,
    tags: agent.tags ? agent.tags.split(',') : [],
    status: agent.status,
    userRegistrationMode: agent.user_registration_mode,
    createdAt: agent.created_at,
    updatedAt: agent.updated_at,
    deletedAt: agent.deleted_at,
  })) : [];
  
  return {
    agents,
    total: agents.length,
    page: params.page || 1,
    pageSize: params.pageSize || 12,
    hasMore: false,
  };
}

export async function getAgent(agentId: string): Promise<Agent> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}`, {
      headers: getAuthHeader(),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch agent: ${response.statusText}`);
  }

  const backendAgent = await response.json();

  // Convert backend format (snake_case, comma-separated tags) to frontend format (camelCase, array)
  return {
    agentId: backendAgent.agent_id,
    ownerId: backendAgent.owner_id,
    name: backendAgent.name,
    displayName: backendAgent.display_name || backendAgent.name,
    description: backendAgent.description,
    tags: backendAgent.tags ? (Array.isArray(backendAgent.tags) ? backendAgent.tags : backendAgent.tags.split(',')) : [],
    status: backendAgent.status,
    userRegistrationMode: backendAgent.user_registration_mode,
    createdAt: backendAgent.created_at,
    updatedAt: backendAgent.updated_at,
    deletedAt: backendAgent.deleted_at,
  };
}

export async function createAgent(data: {
  name: string;
  displayName: string;
  description?: string;
  tags?: string[];
  userRegistrationMode?: string;
  status?: string;
}): Promise<Agent> {
  // Convert frontend format to backend format
  const backendData = {
    name: data.name,
    description: data.description,
    tags: data.tags?.join(','), // Backend expects comma-separated string
    user_registration_mode: data.userRegistrationMode, // Backend uses snake_case
    status: data.status,
  };

  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(backendData),
    })
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create agent');
  }

  // Convert backend response (snake_case) to frontend format (camelCase)
  const backendResponse = await response.json();
  return {
    agentId: backendResponse.agent_id,
    ownerId: backendResponse.owner_id,
    name: backendResponse.name,
    displayName: backendResponse.display_name || backendResponse.name,
    description: backendResponse.description,
    tags: backendResponse.tags ? backendResponse.tags.split(',') : [],
    status: backendResponse.status,
    userRegistrationMode: backendResponse.user_registration_mode,
    createdAt: backendResponse.created_at,
    updatedAt: backendResponse.updated_at,
    deletedAt: backendResponse.deleted_at,
  };
}

export async function updateAgent(agentId: string, data: Partial<Agent>): Promise<Agent> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to update agent: ${response.statusText}`);
  }

  return response.json();
}

export async function deleteAgent(agentId: string): Promise<void> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to delete agent: ${response.statusText}`);
  }
}

// Prompt API
export async function getPrompts(agentId: string): Promise<PromptWithHistory[]> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts`, {
      headers: getAuthHeader(),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch prompts: ${response.statusText}`);
  }

  const apiResponse = await response.json();
  // API returns { prompts: [...] }, extract the prompts array
  return apiResponse.prompts || [];
}

export async function getPrompt(agentId: string, promptName: string, version?: string): Promise<Prompt> {
  const searchParams = version ? `?version=${version}` : '';
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts/${encodeURIComponent(promptName)}${searchParams}`,
      {
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch prompt: ${response.statusText}`);
  }

  return response.json();
}

export async function createPromptVersion(
  agentId: string,
  promptName: string,
  data: { content: string; versionNote?: string }
): Promise<Prompt> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts/${encodeURIComponent(promptName)}/versions`,
      {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify(data),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to create prompt version: ${response.statusText}`);
  }

  return response.json();
}

export async function deployPromptVersion(
  agentId: string,
  promptName: string,
  version: string
): Promise<{ name: string; deployedVersion: string; previousVersion: string; deployedAt: string }> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts/${encodeURIComponent(promptName)}/versions/${encodeURIComponent(version)}/deploy`,
      {
        method: 'POST',
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to deploy prompt version: ${response.statusText}`);
  }

  return response.json();
}

export async function testPrompt(
  agentId: string,
  data: { promptName: string; version: string; message: string; conversationHistory?: unknown[] }
): Promise<PromptTestResponse> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts/test`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to test prompt: ${response.statusText}`);
  }

  return response.json();
}

export async function createPrompt(
  agentId: string,
  data: { name: string; content: string; category?: string }
): Promise<Prompt> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    })
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create prompt' }));
    throw new Error(error.message || 'Failed to create prompt');
  }

  return response.json();
}

export async function deletePrompt(agentId: string, promptName: string): Promise<void> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts/${encodeURIComponent(promptName)}`,
      {
        method: 'DELETE',
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to delete prompt: ${response.statusText}`);
  }
}

export async function updatePrompt(
  agentId: string,
  promptName: string,
  data: { content: string }
): Promise<Prompt> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts/${encodeURIComponent(promptName)}`,
      {
        method: 'PATCH',
        headers: getAuthHeader(),
        body: JSON.stringify(data),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to update prompt: ${response.statusText}`);
  }

  return response.json();
}

export async function getPromptHistory(
  agentId: string,
  promptName: string
): Promise<{ name: string; history: Prompt[]; currentVersion: string }> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/prompts/${encodeURIComponent(promptName)}/history`,
      {
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch prompt history: ${response.statusText}`);
  }

  return response.json();
}

// Agent Settings API
export async function getAgentSettings(agentId: string): Promise<AgentSettings> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/settings`, {
      headers: getAuthHeader(),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch agent settings: ${response.statusText}`);
  }

  return response.json();
}

export async function updateAgentSettings(
  agentId: string,
  data: {
    displayName?: string;
    description?: string;
    tags?: string[];
    userRegistrationMode?: 'auto' | 'whitelist';
    lineIntegration?: {
      channelId?: string;
      channelSecret?: string;
      isEnabled?: boolean;
    };
  }
): Promise<Agent> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/settings`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify(data),
    })
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update agent settings');
  }

  return response.json();
}

export async function updateAgentStatus(
  agentId: string,
  status: 'running' | 'paused' | 'stopped'
): Promise<Agent> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/status`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify({ status }),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to update agent status: ${response.statusText}`);
  }

  return response.json();
}

// Whitelist API
export async function getWhitelistUsers(agentId: string): Promise<WhitelistUser[]> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/whitelist`, {
      headers: getAuthHeader(),
    })
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch whitelist users: ${response.statusText}`);
  }

  return response.json();
}

export async function addWhitelistUser(
  agentId: string,
  userId: string,
  displayName?: string
): Promise<WhitelistUser> {
  const response = await handleResponse(
    await fetchWithErrorHandling(`${API_BASE}/api/agents/${encodeURIComponent(agentId)}/whitelist`, {
      method: 'POST',
      headers: getAuthHeader(),
      body: JSON.stringify({ userId, displayName }),
    })
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to add user to whitelist');
  }

  return response.json();
}

export async function removeWhitelistUser(agentId: string, userId: string): Promise<void> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/whitelist/${encodeURIComponent(userId)}`,
      {
        method: 'DELETE',
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to remove user from whitelist: ${response.statusText}`);
  }
}

// Analytics API

export interface AnalyticsSummary {
  total_messages: number;
  active_users: number;
  avg_response_time_ms: number;
  total_users: number;
  daily_messages: number;
  period: string;
  demo_mode: boolean;
}

export interface AnalyticsTrends {
  period: string;
  daily_messages: Array<{ date: string; messages: number; users: number }>;
  user_growth: Array<{ date: string; total: number; new: number }>;
  summary: { total: number; average: number; trend: string };
  demo_mode: boolean;
}

export interface ResponseTimeDistribution {
  data: Array<{ range: string; count: number; percentage: number }>;
  demo_mode: boolean;
}

export interface HourlyDistribution {
  data: Array<{ hour: string; messages: number }>;
  demo_mode: boolean;
}

export interface TopTopics {
  topics: Array<{ topic: string; count: number; trend: 'up' | 'down' | 'stable' }>;
  summary: { total_topics: number; most_discussed: string | null };
  demo_mode: boolean;
}

export async function getAnalyticsSummary(
  agentId: string,
  period: number = 7,
  demoMode: boolean = false
): Promise<AnalyticsSummary> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/analytics/summary?period=${period}&demo=${demoMode}`,
      {
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch analytics summary: ${response.statusText}`);
  }

  return response.json();
}

export async function getAnalyticsTrends(
  agentId: string,
  period: number = 30,
  demoMode: boolean = false
): Promise<AnalyticsTrends> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/analytics/trends?period=${period}&demo=${demoMode}`,
      {
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch analytics trends: ${response.statusText}`);
  }

  return response.json();
}

export async function getResponseTimeDistribution(
  agentId: string,
  period: number = 30,
  demoMode: boolean = false
): Promise<ResponseTimeDistribution> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/analytics/response-time?period=${period}&demo=${demoMode}`,
      {
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch response time distribution: ${response.statusText}`);
  }

  return response.json();
}

export async function getHourlyDistribution(
  agentId: string,
  period: number = 7,
  demoMode: boolean = false
): Promise<HourlyDistribution> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/analytics/hourly?period=${period}&demo=${demoMode}`,
      {
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch hourly distribution: ${response.statusText}`);
  }

  return response.json();
}

export async function getTopTopics(
  agentId: string,
  period: number = 30,
  demoMode: boolean = false
): Promise<TopTopics> {
  const response = await handleResponse(
    await fetchWithErrorHandling(
      `${API_BASE}/api/agents/${encodeURIComponent(agentId)}/analytics/topics?period=${period}&demo=${demoMode}`,
      {
        headers: getAuthHeader(),
      }
    )
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch top topics: ${response.statusText}`);
  }

  return response.json();
}
